import { ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import { Reveal, RevealMask } from "./motion-primitives";
import { IMAGES } from "./data";

const VIETNAM_CITIES = [
  { name: "Da Nang", to: "/destinations/da-nang" },
  { name: "Ho Chi Minh City", to: "/destinations/ho-chi-minh-city" },
];

const UP_NEXT = [
  {
    no: "02",
    name: "GOA",
    region: "India",
    note: "Local knowledge, built from the inside.",
    status: "Growing",
    to: "/destinations/goa",
  },
  {
    no: "03",
    name: "THAILAND",
    region: "Bangkok · Pattaya · Phuket · Krabi · Koh Lanta",
    note: "",
    status: "Coming next",
    to: null,
  },
];

const UpNextRow = ({ item, delay }) => {
  const inner = (
    <div
      className="group flex flex-wrap items-center justify-between gap-x-6 gap-y-2 border-b border-charcoal py-6 transition-colors hover:border-teal/40"
      data-testid={`upnext-${item.name.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <div className="flex min-w-0 items-baseline gap-5">
        <span className="font-sans text-xs tracking-[0.2em] text-sand">{item.no}</span>
        <div className="min-w-0">
          <div className="flex flex-wrap items-baseline gap-3">
            <span className="font-serif text-2xl font-light text-cream/90 transition-colors group-hover:text-cream sm:text-3xl">
              {item.name}
            </span>
            {item.to && (
              <ArrowUpRight
                size={15}
                strokeWidth={1.6}
                className="text-teal opacity-0 transition-opacity duration-300 group-hover:opacity-100"
              />
            )}
          </div>
          <p className="mt-1 font-sans text-xs uppercase tracking-[0.16em] text-sand">{item.region}</p>
          {item.note && <p className="mt-2 font-sans text-sm text-sand">{item.note}</p>}
        </div>
      </div>
      <span className="shrink-0 font-sans text-[0.62rem] uppercase tracking-[0.24em] text-teal">
        {item.status}
      </span>
    </div>
  );
  return (
    <Reveal delay={delay}>
      {item.to ? (
        <Link to={item.to} className="block">
          {inner}
        </Link>
      ) : (
        inner
      )}
    </Reveal>
  );
};

// Compact editorial split — Vietnam as the single visual hero, upcoming destinations kept light.
export const FeaturedDestinations = () => {
  return (
    <section
      id="destinations"
      data-testid="featured-destinations-section"
      className="border-t border-charcoal bg-charcoal/20"
    >
      <div className="mx-auto max-w-[1400px] px-6 py-16 sm:px-10 lg:px-16 lg:py-20">
        {/* Featured destination — Vietnam */}
        <div
          className="grid grid-cols-1 gap-8 lg:grid-cols-12 lg:items-center lg:gap-14"
          data-testid="featured-destination-vietnam"
        >
          <RevealMask className="lg:col-span-7">
            <div className="group relative aspect-[16/10] w-full overflow-hidden sm:aspect-[3/2] lg:aspect-auto lg:h-[440px]">
              <img
                src={IMAGES.vietnam}
                alt="Vietnam coastline at golden hour"
                className="h-full w-full object-cover object-[center_55%] transition-transform duration-[1200ms] ease-out group-hover:scale-[1.03]"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-ink/40 to-transparent" />
              <span className="absolute left-6 top-6 font-sans text-[0.65rem] uppercase tracking-[0.3em] text-cream/70">
                01 / Active
              </span>
            </div>
          </RevealMask>

          <div className="lg:col-span-5">
            <Reveal>
              <span className="font-sans text-xs uppercase tracking-[0.3em] text-teal">
                Featured destination
              </span>
            </Reveal>
            <Reveal delay={0.06}>
              <h2 className="mt-4 font-serif text-6xl font-light leading-none tracking-tight text-cream sm:text-7xl">
                Vietnam
              </h2>
            </Reveal>
            <Reveal delay={0.12}>
              <p className="mt-5 max-w-md font-sans text-lg leading-relaxed text-sand">
                Curated guides from the coast to the city.
              </p>
            </Reveal>
            <Reveal delay={0.18}>
              <div className="mt-8">
                <p className="font-sans text-[0.62rem] uppercase tracking-[0.24em] text-sand">
                  Currently covered
                </p>
                <div className="mt-3 flex flex-wrap gap-x-6 gap-y-2">
                  {VIETNAM_CITIES.map((c) => (
                    <Link
                      key={c.name}
                      to={c.to}
                      data-testid={`dest-city-${c.name.toLowerCase().replace(/\s+/g, "-")}`}
                      className="group inline-flex items-center gap-1.5 font-serif text-xl font-light text-cream transition-colors hover:text-teal"
                    >
                      {c.name}
                      <ArrowUpRight
                        size={14}
                        strokeWidth={1.6}
                        className="text-teal transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                      />
                    </Link>
                  ))}
                </div>
              </div>
            </Reveal>
            <Reveal delay={0.24}>
              <Link
                to="/destinations/da-nang"
                data-testid="destination-vietnam-cta"
                className="group mt-10 inline-flex w-fit items-center gap-3 border-b border-teal pb-2 font-sans text-sm uppercase tracking-[0.18em] text-cream transition-colors hover:text-teal"
              >
                Explore Da Nang
                <ArrowUpRight
                  size={16}
                  strokeWidth={1.6}
                  className="text-teal transition-transform duration-500 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                />
              </Link>
            </Reveal>
          </div>
        </div>

        {/* Up next — lighter weight */}
        <div className="mt-12 lg:mt-14">
          <Reveal>
            <span className="font-sans text-xs uppercase tracking-[0.3em] text-sand">Up next</span>
          </Reveal>
          <div className="mt-6">
            {UP_NEXT.map((item, i) => (
              <UpNextRow key={item.name} item={item} delay={i * 0.08} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
